import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import React, { PureComponent } from "react";

import { DateRange } from "react-date-range";
import { connect } from "react-redux";
import { differenceInDays } from "date-fns";
import styled from "styled-components/macro";
import {
  SettingsPanelContainerRow,
  ModalContainer,
  ModalBackdrop,
  VerticalFlex,
  SettingsPanelContainerColumn,
  ErrorElem,
  FlexItem,
  SettingsPanelSpan,
} from "../common/StyledComponents";
import { IconSettingsButton33x32, SettingsButton } from "../common/Buttons";

import { withTranslation, WithTranslation } from "react-i18next";

import calendarIcon from "../../../icons/ButtonDatePicker.png";
import calendarIconHover from "../../../icons/ButtonDatePickerhover.png";
import calendarIconPressed from "../../../icons/ButtonDatePickerPressed.png";
import IconCurrentDay from "../../../icons/ButtonCurrentDay.png";
import IconCurrentDayHover from "../../../icons/ButtonCurrentDayHover.png";
import IconCurrentDayPressed from "../../../icons/ButtonCurrentDayPressed.png";
import { getValidatedDatesFromInput, getFormattedDateText, dateRangeCurrentLocale, DATERANGE_SEPARATOR } from "../../helpers/DateHelpers";
import { KEYCODE_ENTER, CALENDAR_SELECTION } from "../../helpers/Const";
import { iRootState, Dispatch } from "src/rematch/store";
import { Action } from "@rematch/core";

const imgs = [calendarIcon, calendarIconHover, calendarIconPressed, IconCurrentDay, IconCurrentDayHover, IconCurrentDayPressed];

export const DateRangeContainer = styled.div`
  margin-bottom: 10px;
  flex: 1 1 auto;
`;

export const DateInput = styled.input`
  flex: 1 1 auto;
  height: 32px;
  border: 1px solid #3a4855;
  border-right: 0px;
  padding-left: 10px;
`;

interface IDateRangePickerWrapperProps extends WithTranslation {
  startOfWeekDay: number;
  dateFormat: string;
  startDate: Date;
  endDate: Date;
  scrollToCurrentDay: () => void;
  onApplyDateRange: (startDate: Date, endDate: Date) => Action<[Date, Date],void>;
  clearUndo: () => Action<void,void>;
}

interface IDateRangePickerWrapperState {
  preliminaryStartDate: Date;
  preliminaryEndDate: Date;
  showDateRange: boolean;
  customDateRange: string;
  prevDateRange: string;
  prevLanguage: string;
  errorState: string;
}

class DateRangePickerWrapper extends PureComponent<IDateRangePickerWrapperProps,IDateRangePickerWrapperState> {
  constructor(props: IDateRangePickerWrapperProps) {
    super(props);
    const dateText = getFormattedDateText(this.props.startDate, this.props.endDate, this.getDateFormat());
    this.state = {
      preliminaryStartDate: this.props.startDate,
      preliminaryEndDate: this.props.endDate,
      showDateRange: false,
      customDateRange: dateText,
      prevDateRange: dateText,
      prevLanguage: this.props.i18n.language,
      errorState: null,
    };
  }

  componentDidMount = () => {
    for (const imgURL of imgs) {
      const img = new Image();
      img.src = imgURL;
    }
  };

  componentDidUpdate = (prevProps: IDateRangePickerWrapperProps) => {
    if(prevProps.dateFormat !== this.props.dateFormat || this.state.prevLanguage !== this.props.i18n.language) {
      const dateText = getFormattedDateText(this.state.preliminaryStartDate, this.state.preliminaryEndDate, this.getDateFormat());
      this.setState(() => {
        return {
          customDateRange: dateText,
          prevDateRange: dateText,
          prevLanguage: this.props.i18n.language,
        }
      });
    }
  };

  onDateTimeInputChange = (e) => {
    const inputVal = (e.target as HTMLInputElement).value;
    const dateObj = getValidatedDatesFromInput(inputVal, this.getDateFormat());
    this.setState(() => {
      return {
        customDateRange: inputVal,
        preliminaryStartDate: dateObj.start ? dateObj.start : this.state.preliminaryStartDate,
        preliminaryEndDate: dateObj.end ? dateObj.end : this.state.preliminaryEndDate,
        errorState: dateObj.error,
      };
    });
  };

  onDateInputBlur = (e) => {
    const inputVal = (e.target as HTMLInputElement).value;
    const dates = getValidatedDatesFromInput(inputVal, this.getDateFormat());
    const { t } = this.props;
    if(dates.error === undefined && this.state.prevDateRange !== inputVal) {
      this.setState(() => {
        return {
          prevDateRange: inputVal,
        };
      });
      if (differenceInDays(dates.end, dates.start) > 10 * 365) {
        this.showError(t("error.maxyearsduration", {number: 10}));
      } else {
        this.props.clearUndo();
        this.props.onApplyDateRange(dates.start, dates.end);
      }
    }
  };

  applyCustomDateRange = (e) => {
    if (e.which !== KEYCODE_ENTER || this.state.customDateRange === getFormattedDateText(this.props.startDate, this.props.endDate, this.getDateFormat())) {
      return;
    }
    const dates = getValidatedDatesFromInput(this.state.customDateRange, this.getDateFormat());
    const { t } = this.props;
    if (dates.error === undefined) {
      if (differenceInDays(dates.end, dates.start) > 10 * 365) {
        this.showError(t("error.maxyearsduration", {number: 10}));
      } else {
        this.props.clearUndo();
        this.props.onApplyDateRange(dates.start, dates.end);
      }
    } else {
      this.showError(dates.error);
    }
  };

  showError = (errorMsg) => {
    this.setState(() => {
      return {
        errorState: errorMsg,
      };
    });
  };

  changeDateRange = ({ dateRange }) => {
    this.setState(() => {
      return {
        preliminaryStartDate: dateRange.startDate,
        preliminaryEndDate: dateRange.endDate,
      };
    });
  };

  cancelDateRange = () => {
    const dateText = getFormattedDateText(this.props.startDate, this.props.endDate, this.getDateFormat());
    this.setState(() => ({
      preliminaryStartDate: this.props.startDate,
      preliminaryEndDate: this.props.endDate,
      customDateRange: dateText,
    }));
    this.toggleDateRangeVisibility();
  };

  applyDateRange = () => {
    this.props.clearUndo();
    this.props.onApplyDateRange(this.state.preliminaryStartDate, this.state.preliminaryEndDate);
    this.toggleDateRangeVisibility();
    const dateText = getFormattedDateText(this.state.preliminaryStartDate, this.state.preliminaryEndDate, this.getDateFormat());
    this.setState(() => ({ customDateRange: dateText }));
  };

  toggleDateRangeVisibility = () => {
    this.setState((prevState) => {
      return { showDateRange: !prevState.showDateRange };
    });
  };

  getDateFormat() {
    //Show date only
    return this.props.dateFormat;
  }

  renderDateRange = () => {
    const selectionRange = {
      startDate: this.state.preliminaryStartDate,
      endDate: this.state.preliminaryEndDate,
      key: "dateRange",
    };
    const { t } = this.props;
    return (
      <div>
        <ModalContainer position="absolute">
          <VerticalFlex style={{ margin: 20 }}>
            <DateRangeContainer>
              <DateRange
                ranges={[selectionRange]}
                onChange={this.changeDateRange}
                months={2}
                direction="horizontal"
                dateDisplayFromat={this.getDateFormat()}
                singleDateRange={true}
                rangeColors={[CALENDAR_SELECTION]}
                locale={dateRangeCurrentLocale()}
                weekStartsOn={this.props.startOfWeekDay}
              />
            </DateRangeContainer>
            <FlexItem>
              <SettingsButton onClick={this.cancelDateRange}>{t("command.cancel")}</SettingsButton>
              <SettingsButton onClick={this.applyDateRange}>{t("command.apply")}</SettingsButton>
            </FlexItem>
          </VerticalFlex>
        </ModalContainer>
        <ModalBackdrop onClick={this.toggleDateRangeVisibility}></ModalBackdrop>
      </div>
    );
  };

  render = () => {
    const { t } = this.props;
    return (
      <SettingsPanelContainerColumn>
        <SettingsPanelContainerRow>
          <SettingsPanelSpan>{t("pageheader.daterange")}</SettingsPanelSpan>
          {this.state.errorState ? <ErrorElem style={{ paddingBottom: 3 }}>{this.state.errorState}</ErrorElem> : null}
        </SettingsPanelContainerRow>
        <SettingsPanelContainerRow style={{boxShadow: this.state.errorState ? "0px 0px 5px red" : undefined}}>
          <DateInput
            onKeyPress={this.applyCustomDateRange}
            onChange={this.onDateTimeInputChange}
            onBlur={this.onDateInputBlur}
            value={this.state.customDateRange}
            placeholder={this.getDateFormat() + DATERANGE_SEPARATOR + this.getDateFormat()}
          ></DateInput>
          <IconSettingsButton33x32
            onClick={this.toggleDateRangeVisibility}
            image={calendarIcon}
            imageHover={calendarIconHover}
            imagePressed={calendarIconPressed}
          ></IconSettingsButton33x32>

          <IconSettingsButton33x32
            onClick={this.props.scrollToCurrentDay}
            image={IconCurrentDay}
            imageHover={IconCurrentDayHover}
            imagePressed={IconCurrentDayPressed}
            style={{ marginLeft: 10 }}
          />
        </SettingsPanelContainerRow>
        {this.state.showDateRange ? this.renderDateRange() : ""}
      </SettingsPanelContainerColumn>
    );
  };
}

export default withTranslation()(connect(
  (rootState: iRootState) => ({
    startOfWeekDay: rootState.UI.displaySettings.startOfWeekDay,
    dateFormat: rootState.UI.displaySettings.dateFormat,
    startDate: rootState.items.dateRange[0],
    endDate: rootState.items.dateRange[1],
  }),
  (dispatch: Dispatch) => ({
    onApplyDateRange: (startDate: Date, endDate: Date) => dispatch.items.updateDateRangeAsync([startDate, endDate]),
    clearUndo: dispatch.history.clear,
  })
)((props: IDateRangePickerWrapperProps) => <DateRangePickerWrapper {...props} key={`${props.startDate}-${props.endDate}`} />));
